/**
 * Geometria plana minima, sin dependencias: areas, centroides, cajas, punto en poligono.
 * Los anillos son listas de [x, y]; un poligono es una lista de anillos (el primero exterior,
 * el resto huecos); un multipoligono es una lista de poligonos. Es el mismo modelo que GeoJSON.
 */
export type Punto = [number, number];
export type Anillo = Punto[];
export type Poligono = Anillo[];
export type MultiPoligono = Poligono[];
export type Bbox = [number, number, number, number];

/** Area con signo (positiva en sentido antihorario). */
export function areaAnilloConSigno(anillo: Anillo): number {
  let s = 0;
  const n = anillo.length;
  if (n < 3) return 0;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = anillo[i];
    const [x2, y2] = anillo[(i + 1) % n];
    s += x1 * y2 - x2 * y1;
  }
  return s / 2;
}

export function areaAnillo(anillo: Anillo): number {
  return Math.abs(areaAnilloConSigno(anillo));
}

/** Area del poligono: exterior menos huecos. */
export function areaPoligono(poligono: Poligono): number {
  if (poligono.length === 0) return 0;
  let a = areaAnillo(poligono[0]);
  for (let i = 1; i < poligono.length; i++) a -= areaAnillo(poligono[i]);
  return Math.max(0, a);
}

export function areaMultiPoligono(mp: MultiPoligono): number {
  return mp.reduce((s, p) => s + areaPoligono(p), 0);
}

/** Centroide del anillo exterior (formula del area); si el area es nula, media de vertices. */
export function centroideAnillo(anillo: Anillo): Punto {
  const a = areaAnilloConSigno(anillo);
  const n = anillo.length;
  if (n === 0) return [0, 0];
  if (Math.abs(a) < 1e-9) {
    let sx = 0, sy = 0;
    for (const [x, y] of anillo) { sx += x; sy += y; }
    return [sx / n, sy / n];
  }
  let cx = 0, cy = 0;
  for (let i = 0; i < n; i++) {
    const [x1, y1] = anillo[i];
    const [x2, y2] = anillo[(i + 1) % n];
    const f = x1 * y2 - x2 * y1;
    cx += (x1 + x2) * f;
    cy += (y1 + y2) * f;
  }
  return [cx / (6 * a), cy / (6 * a)];
}

/** Centroide de un multipoligono ponderado por el area de cada exterior. */
export function centroideMultiPoligono(mp: MultiPoligono): Punto {
  let sx = 0, sy = 0, sa = 0;
  for (const p of mp) {
    if (p.length === 0) continue;
    const a = areaPoligono(p);
    const [cx, cy] = centroideAnillo(p[0]);
    sx += cx * a; sy += cy * a; sa += a;
  }
  if (sa === 0) {
    const primero = mp[0]?.[0];
    return primero ? centroideAnillo(primero) : [0, 0];
  }
  return [sx / sa, sy / sa];
}

export function bboxDePuntos(puntos: Iterable<Punto>): Bbox {
  let minx = Infinity, miny = Infinity, maxx = -Infinity, maxy = -Infinity;
  for (const [x, y] of puntos) {
    if (x < minx) minx = x;
    if (y < miny) miny = y;
    if (x > maxx) maxx = x;
    if (y > maxy) maxy = y;
  }
  return [minx, miny, maxx, maxy];
}

export function bboxMultiPoligono(mp: MultiPoligono): Bbox {
  const puntos: Punto[] = [];
  for (const p of mp) for (const a of p) for (const v of a) puntos.push(v);
  return bboxDePuntos(puntos);
}

export function bboxesSolapan(a: Bbox, b: Bbox): boolean {
  return a[0] <= b[2] && a[2] >= b[0] && a[1] <= b[3] && a[3] >= b[1];
}

export function areaBbox(b: Bbox): number {
  return Math.max(0, b[2] - b[0]) * Math.max(0, b[3] - b[1]);
}

/** Punto dentro de anillo (ray casting; el borde cuenta como dentro de forma aproximada). */
export function puntoEnAnillo(p: Punto, anillo: Anillo): boolean {
  const [x, y] = p;
  let dentro = false;
  const n = anillo.length;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const [xi, yi] = anillo[i];
    const [xj, yj] = anillo[j];
    const cruza = (yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (cruza) dentro = !dentro;
  }
  return dentro;
}

export function puntoEnPoligono(p: Punto, poligono: Poligono): boolean {
  if (poligono.length === 0 || !puntoEnAnillo(p, poligono[0])) return false;
  for (let i = 1; i < poligono.length; i++) if (puntoEnAnillo(p, poligono[i])) return false;
  return true;
}

export function puntoEnMultiPoligono(p: Punto, mp: MultiPoligono): boolean {
  return mp.some((pol) => puntoEnPoligono(p, pol));
}

export function distancia(a: Punto, b: Punto): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1]);
}

/** Longitud de una polilinea abierta. */
export function longitud(puntos: Punto[]): number {
  let s = 0;
  for (let i = 1; i < puntos.length; i++) s += distancia(puntos[i - 1], puntos[i]);
  return s;
}

/** Rectangulo como anillo cerrado (5 puntos, el ultimo igual al primero). */
export function anilloDeBbox(b: Bbox): Anillo {
  return [[b[0], b[1]], [b[2], b[1]], [b[2], b[3]], [b[0], b[3]], [b[0], b[1]]];
}

/** Un rectangulo y un poligono se tocan si comparten algun vertice interior o se cruzan sus cajas y centros. */
export function rectanguloTocaPoligono(b: Bbox, poligono: Poligono): boolean {
  if (poligono.length === 0) return false;
  const cajaPol = bboxDePuntos(poligono[0]);
  if (!bboxesSolapan(b, cajaPol)) return false;
  const esquinas: Punto[] = [[b[0], b[1]], [b[2], b[1]], [b[2], b[3]], [b[0], b[3]], [(b[0] + b[2]) / 2, (b[1] + b[3]) / 2]];
  if (esquinas.some((e) => puntoEnPoligono(e, poligono))) return true;
  for (const v of poligono[0]) {
    if (v[0] >= b[0] && v[0] <= b[2] && v[1] >= b[1] && v[1] <= b[3]) return true;
  }
  // Aristas del poligono que cruzan el rectangulo sin vertices dentro: se muestrea cada arista.
  const ext = poligono[0];
  for (let i = 0; i < ext.length; i++) {
    const p = ext[i], q = ext[(i + 1) % ext.length];
    for (let t = 0.1; t < 1; t += 0.1) {
      const x = p[0] + (q[0] - p[0]) * t, y = p[1] + (q[1] - p[1]) * t;
      if (x >= b[0] && x <= b[2] && y >= b[1] && y <= b[3]) return true;
    }
  }
  return false;
}
